import styled, { css } from '@xstyled/styled-components';
import React, { FC, useCallback, useEffect } from 'react';
import PointerImg from './bubble-pointer.svg';
import FullLlamaImg from './llama-circle.svg';
import { DefaultLlama } from './llamas/default';
import { SadLlama } from './llamas/sad';
import { TrainingState, useTrainingStore } from '~/training/state';
import { select } from '~/lib/select';

/*

  Unfortunately lots of CSS in here, that's to position the llama and the modal in various situations...

*/

export interface Props {
  horizontal?: 'left' | 'center' | 'right';
  vertical?: 'top' | 'center' | 'bottom';
  close?: () => void;
  variant?: 'sad' | 'default';
  showLlama?: 'show' | 'hide';
  /** Allow vertical scroll on short viewports (e.g. onboarding). */
  allowScroll?: boolean;
}

// correspondance between alignment values and the flex property values
const vtable = {
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end'
};

const htable = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end'
};

const FullLlama = styled(FullLlamaImg)<Props>`
  position: absolute;
  bottom: -20px;
  margin-left: 0px;
  z-index: 3;
  left: 75px;

  @media (min-width: sm) and (max-width: 1024px) {
    left: 160px;
  }

  @media (max-width: sm) {
    z-index: -1;
    top: 0px;
    left: 50px;
  }
`;

// The llama picture

// The modal itself
const Modal = styled.div`
  background: ${p => p.theme.colors.white};
  border-radius: lg;
  position: relative;
  z-index: 2;
  max-width: min(100%, calc(100vw - 2rem));
  box-sizing: border-box;
  /* Keep overflow visible so Pointer (speech-bubble tail) can paint outside. */
  overflow: visible;
  @media (max-width: sm) {
    width: 100%;
  }
`;

// This is the pointer thingy that comes from the modal and points at the llama
const Pointer = styled(PointerImg)<Props>`
  position: absolute;
  height: 30px;

  ${p =>
    p.horizontal === 'right'
      ? css`
          right: 40px;
          transform: scaleX(-1) rotate(0deg);
          bottom: -29px;
        `
      : p.horizontal === 'center'
        ? css`
            top: 100%;
            left: 50px;
          `
        : css`
            top: 50px;
            left: -27px;
            transform: rotate(90deg);
          `}

  @media (min-width: sm) and (max-width: 1024px) {
    ${p =>
      p.horizontal == 'center' &&
      css`
        left: 40%;
      `}
  }

  @media (max-width: sm) {
    top: unset;
    bottom: 100%;

    ${p =>
      p.horizontal === 'right'
        ? css`
            right: 160px;
            transform: scaleX(-1) scaleY(-1);
          `
        : css`
            left: 160px;
            transform: scaleY(-1);
          `}
  }
`;

// The modal's content so it can scroll (200% zoom / short viewports — EL–E18)
const ModalContent = styled.div`
  min-width: 50px;
  min-height: 0;
  max-height: calc(100dvh - 8rem);
  /* Clip overflowing menu rows at 400% zoom without cutting Modal's Pointer. */
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;

  @media (max-height: 400px) {
    /* Wrapper padding + llama eat most of a short CSS viewport (400% zoom) */
    max-height: calc(100dvh - 2.5rem);
  }
`;

// This element adds padding to keep the llama on screen regardless of the modal's size
const Wrapper = styled.div<Props>`
  display: flex;
  position: relative;
  max-height: 100%;
  min-height: 0;
  box-sizing: border-box;
  ${p =>
    p.horizontal === 'right'
      ? css`
          padding-right: 60px;
          padding-top: 60px;
        `
      : p.horizontal === 'center'
        ? css`
            padding-left: 135px;
            padding-bottom: 100px;
          `
        : css`
            padding-left: 135px;
            padding-top: 60px;
          `}
  margin-left: ${p => (p.horizontal === 'center' ? '-150px' : 'unset')};

  @media (max-width: sm) {
    padding: 0;
    margin: 0;
    padding-top: 150px;
    width: 100%;
  }

  @media (max-height: 400px) {
    padding-top: 12px;
    padding-right: 12px;
    padding-left: 12px;
    padding-bottom: 12px;
    margin-left: 0;
  }
`;

// This positions the modal on the screen
const Overlay = styled.div<Props>`
  position: fixed;
  display: flex;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: md;
  align-items: ${p => vtable[p.vertical!]};
  justify-content: ${p => htable[p.horizontal!]};
  pointer-events: none;
  overflow-x: hidden;
  overflow-y: ${p => (p.allowScroll ? 'auto' : 'hidden')};

  & > * {
    pointer-events: all;
  }

  @media (max-width: sm) {
    align-items: flex-end;
    justify-content: stretch;
  }
`;

const selector = (s: TrainingState) => select(s, 'featureShowLlama');

export const LlamaModal: FC<Props> = ({
  showLlama,
  children,
  close,
  allowScroll,
  ...props
}) => {
  const { featureShowLlama } = useTrainingStore(selector);

  const escFunction = useCallback(event => {
    if (event.keyCode === 27) {
      if (close) {
        close();
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction);

    return () => {
      document.removeEventListener('keydown', escFunction);
    };
  }, [escFunction]);

  return (
    <Overlay {...props} allowScroll={allowScroll} className="LlamaModalOverlay">
      <Wrapper
        {...props}
        onClick={e => e.stopPropagation()}
        className="LlamaModalWrapper"
      >
        {showLlama == 'hide'
          ? false
          : props.horizontal === 'right'
            ? props.variant === 'sad'
              ? featureShowLlama && (
                  // CAT-1292 / EL–E189: decorative illustration — hide from AT.
                  <SadLlama
                    {...props}
                    onClick={close}
                    aria-hidden="true"
                    focusable="false"
                  />
                )
              : featureShowLlama && (
                  <DefaultLlama
                    {...props}
                    onClick={close}
                    aria-hidden="true"
                    focusable="false"
                  />
                )
            : featureShowLlama && (
                <FullLlama
                  {...props}
                  onClick={close}
                  aria-hidden="true"
                  focusable="false"
                />
              )}
        <Modal className="modalStyleForDarkTheme">
          {showLlama == 'hide'
            ? false
            : featureShowLlama && (
                <Pointer
                  {...props}
                  className="pointerStyleForDarkTheme"
                  aria-hidden="true"
                  focusable="false"
                />
              )}
          <ModalContent>{children}</ModalContent>
        </Modal>
      </Wrapper>
    </Overlay>
  );
};

LlamaModal.defaultProps = {
  horizontal: 'center',
  vertical: 'center'
};
